import { derived } from 'svelte/store';
import { createSingleRaceFeed } from '../stores.js';
import { findElementInShadowDom, renderSvelteComponent } from './render.js';
import RaceList from '../components/RaceList.svelte';
import { sharedRaceFile } from '../shared-races.js';

/**
 * Finds a single-race embed's box and fills it with that race, from the
 * race's own file (RACE_URL). Updates when the file polls.
 *
 * TO ADD A NEW SINGLE-RACE EMBED:
 * 1. Copy scripts/s-ag.js, rename it, and change the race id and caption.
 * 2. Add its name (without .js) to `entrypoints` in baker.config.js.
 * 3. Add an entry to _data/embeds/races.json with `slug`, `js` (the file
 *    name), `unique_identifier` ('s-' + the race id) and `caption`, plus
 *    `source: "lavote"` for L.A. Vote races (AP races can leave it out).
 *
 * `caption` and `source` come from races.json; race_template.html writes them
 * on the box as data-caption and data-source. Without a caption, RaceList
 * shows the feed's title. Without a source, a race listed in
 * scripts/shared-races.js loads from the source its `use` picks (still found
 * by its AP id here and in races.json); every other race is read as AP.
 *
 * STORY PAGE VERSION: a second embed for the same race, e.g. under a story's
 * headline. Story stream embeds start with s-; story page ones don't.
 * 1. Copy scripts/embeds-la-mayor.js, rename it, and change the race id and
 *    embedName ('results-' + the race id).
 * 2. Add its name to `entrypoints` in baker.config.js.
 * 3. Add a races.json entry with `slug`, `js`, `unique_identifier` (the same
 *    embedName), plus any of:
 *      "legend": true            ✓ Winner / * Incumbent line above the table
 *      "hideCaption": true       no caption above the table
 *      "storyStreamStyle": false skip embed.scss, which gives the story stream
 *                                look (tighter rows, smaller "% of expected
 *                                vote in" line, caption spacing)
 *
 * CAPTION NOTE: a grey line under the caption (e.g. how the race is won).
 * There's only one race, so pass the text itself, no race id needed:
 *
 *   mountSingleRace('CA-6123', { captionNote: 'The top vote-getter wins.' });
 *
 * @param {string} raceId - e.g. 'CA-6482'
 * @param {Object} [options]
 * @param {string} [options.captionNote] - Grey note under the caption
 * @param {string} [options.embedName] - The box's data-embed-name
 *   (races.json `unique_identifier`). Defaults to 's-' + raceId.
 */
export async function mountSingleRace(
  raceId,
  { captionNote, embedName = `s-${raceId}` } = {}
) {
  const container = findElementInShadowDom(`[data-embed-name='${embedName}']`);
  if (!container) return;

  const caption = container.dataset.caption;
  // data-hide-caption is set (as an empty string) when races.json has hideCaption
  const showCaption = container.dataset.hideCaption === undefined;
  // 'ap' or 'lavote': the race file is races/{source}_{raceId}_{date}.json.
  // A source set in races.json wins; otherwise races in shared-races.js
  // follow their `use`, and everything else is AP.
  const [id, source] = container.dataset.source
    ? [raceId, container.dataset.source]
    : sharedRaceFile(raceId);

  const race = await createSingleRaceFeed(id, source);
  // RaceList draws a list of races, so wrap the one race in a list
  const races = derived(race, ($race) => [$race]);

  renderSvelteComponent(RaceList, container, {
    races,
    component: 'result',
    showCaption,
    // keyed by the id of the race that loaded (L.A. Vote's if it was switched)
    captions: caption ? { [id]: caption } : {},
    captionNote: captionNote ? { [id]: captionNote } : {},
  });
}
